/* eslint-disable react-hooks/rules-of-hooks */
import {
  Geometry2d,
  HTMLContainer,
  Rectangle2d,
  ShapeUtil,
  SvgExportContext,
  T,
  TLOnBeforeUpdateHandler,
  TLOnResizeHandler,
  TLOnTranslateHandler,
  Vec2d,
  toDomPrecision,
} from '@tldraw/tldraw'
import { GeoShape, GeoShapeProps } from './Types'
import {
  CornerRoundingStyle,
  FillStyle,
  GeoStyle,
  ShadowStyle,
  StrokeColorStyle,
  StrokeWidthStyle,
} from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import { geoShapeMigrations } from './GeoShape'
import { getShadow } from './getShadow'
import { getFillGradient } from './getFillGradient'
import { svgDefs } from './svgDefs'
import { SVGCommand, parsePath, roundCommands } from 'svg-round-corners'
import { CustomEqualitySet } from 'components/VisualStudioPlayground/VisualEditor/utils/customEqualitySet'
import { isEqual } from 'lodash'
import { GradientColor, SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { isFullyTransparent } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export class GeoShapeUtil extends ShapeUtil<GeoShape> {
  static type = 'geometric'

  static override migrations = geoShapeMigrations

  static override props = {
    geo: GeoStyle,
    strokeColor: StrokeColorStyle,
    strokeWidth: StrokeWidthStyle,
    w: T.positiveNumber,
    h: T.positiveNumber,
    cornerRounding: CornerRoundingStyle,
    shadow: ShadowStyle,
    fill: FillStyle,
    isEditable: T.boolean,
    isAutoUpdatable: T.boolean,
  }

  shouldDeselectAfterTranslating = false

  getDefaultProps(): GeoShapeProps {
    return {
      geo: 'rectangle',
      w: 50,
      h: 50,
      strokeColor: 'black',
      strokeWidth: 2,
      cornerRounding: 0,
      shadow: null,
      fill: { type: 'solidColor', color: '#FFFFFF', opacity: 1 },
      isEditable: true,
      isAutoUpdatable: true,
    }
  }

  getGeometry(shape: GeoShape): Geometry2d {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: !isFullyTransparent(shape.props.fill),
    })
  }

  getPath(shape: GeoShape): string {
    const { geo, h, w, cornerRounding } = shape.props

    let path = ''
    const doc = new DOMParser().parseFromString(svgDefs[geo], 'image/svg+xml')
    const svg = doc.querySelector('svg') as SVGElement
    const originalSvgWidth = parseFloat(svg.getAttribute('width') || '0')
    const originalSvgHeight = parseFloat(svg.getAttribute('height') || '0')
    svg.setAttribute('width', w.toString())
    svg.setAttribute('height', h.toString())
    const childPath = doc.querySelector('g')?.children[0]
    if (!childPath) return path
    const commands = parsePath(childPath.getAttribute('d') || '')
    const scaledCommands = commands.map((command) => {
      if (!command.values) return command
      const values: SVGCommand['values'] = {}
      if ('x' in command.values) values.x = (command.values.x / originalSvgWidth) * w
      if ('y' in command.values) values.y = (command.values.y / originalSvgHeight) * h
      if ('x1' in command.values) values.x1 = (command.values.x1 / originalSvgWidth) * w
      if ('y1' in command.values) values.y1 = (command.values.y1 / originalSvgHeight) * h
      if ('x2' in command.values) values.x2 = (command.values.x2 / originalSvgWidth) * w
      if ('y2' in command.values) values.y2 = (command.values.y2 / originalSvgHeight) * h
      return { ...command, values }
    })
    const scaledRoundedCommands = roundCommands(scaledCommands, cornerRounding)
    path = scaledRoundedCommands.path

    return path
  }

  getColors = (shape: GeoShape) => {
    const colors = new CustomEqualitySet<SolidColor | GradientColor>(isEqual)
    const { fill, strokeWidth, strokeColor } = shape.props

    colors.add(fill)

    if (strokeWidth > 0) {
      colors.add({ type: 'solidColor', color: strokeColor, opacity: 1 })
    }

    return colors
  }

  isColorisable = () => true

  component(shape: GeoShape) {
    const {
      id,
      props: { w, h, strokeColor, strokeWidth, fill },
    } = shape
    const path = this.getPath(shape)
    const shadow = getShadow(shape)
    const fillGradient = getFillGradient(shape)
    const { isMobile } = useResponsiveDesign()

    return (
      <HTMLContainer
        id={id}
        style={{
          width: w,
          height: h,
          pointerEvents: 'all',
        }}
      >
        <div
          style={{ width: w, height: h }}
          onPointerDown={() => {
            if (isMobile) {
              const shapeUtil = this.editor.getShapeUtil('geometric') as GeoShapeUtil
              shapeUtil.shouldDeselectAfterTranslating = this.editor.getSelectedShapes().length === 0
            }
          }}
        >
          <svg
            style={{ transform: `translate(${shadow.svgTranslate.x}px, ${shadow.svgTranslate.y}px` }}
            className="tl-svg-container"
            width={w + shadow.svgDimensions.width}
            height={h + shadow.svgDimensions.height}
          >
            <g filter={shape.props.shadow ? `url(#${shadow.id})` : undefined}>
              <path
                d={path}
                fill={fill.type === 'linearGradient' ? `url('#${fillGradient.id}')` : fill.color}
                fillOpacity={fill.type === 'linearGradient' ? 1 : fill.opacity}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                style={{
                  transform: `translate(${shadow.pathTranslate.x}px, ${shadow.pathTranslate.y}px)`,
                }}
              />
            </g>
            <defs>
              {shadow.defs}
              {fillGradient.defs}
            </defs>
          </svg>
        </div>
      </HTMLContainer>
    )
  }

  override toSvg(shape: GeoShape, ctx: SvgExportContext) {
    const {
      props: { fill, strokeColor, strokeWidth, shadow },
    } = shape

    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')

    path.setAttribute('d', this.getPath(shape))
    if (fill.type === 'linearGradient') {
      const { id, svgExportDef } = getFillGradient(shape)
      path.setAttribute('fill', `url(#${id})`)
      ctx.addExportDef(svgExportDef)
    } else {
      path.setAttribute('fill', fill.color)
      path.setAttribute('fill-opacity', `${fill.opacity}`)
    }
    path.setAttribute('stroke', strokeColor)
    path.setAttribute('stroke-width', `${strokeWidth}`)

    if (shadow) {
      const { id, svgExportDef } = getShadow(shape)
      const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter')
      filter.setAttribute('id', id)
      ctx.addExportDef(svgExportDef)
      g.setAttribute('filter', `url(#${id})`)
    }

    g.appendChild(path)

    return g
  }

  indicator(shape: GeoShape) {
    if (this.editor.getInstanceState().isReadonly) return null
    const bounds = this.editor.getShapeGeometry(shape).bounds
    return <rect width={toDomPrecision(bounds.width)} height={toDomPrecision(bounds.height)} />
  }

  override canEdit = () => true

  override onBeforeUpdate: TLOnBeforeUpdateHandler<GeoShape> = (prev, next) => {
    if (!prev.props.isEditable) {
      return { ...prev, props: { ...prev.props, isEditable: next.props.isEditable } }
    }

    return next
  }

  onTranslate: TLOnTranslateHandler<GeoShape> = (initialShape) => {
    if (!initialShape.props.isEditable) {
      this.editor.snaps.clear()
    }
  }

  override onResize: TLOnResizeHandler<GeoShape> | undefined = (shape, info) => {
    const { initialShape, scaleX, scaleY, handle, newPoint } = info
    let w = initialShape.props.w * scaleX
    let h = initialShape.props.h * scaleY
    let overShrinkX = 0
    let overShrinkY = 0
    const offset = new Vec2d(0, 0)

    // x offsets

    if (scaleX < 0) {
      offset.x += w
    }

    if (handle === 'left' || handle === 'top_left' || handle === 'bottom_left') {
      offset.x += scaleX < 0 ? overShrinkX : -overShrinkX
    }

    // y offsets

    if (scaleY < 0) {
      offset.y += h
    }

    if (handle === 'top' || handle === 'top_left' || handle === 'top_right') {
      offset.y += scaleY < 0 ? overShrinkY : -overShrinkY
    }

    const { x, y } = offset.rot(shape.rotation).add(newPoint)

    return {
      x,
      y,
      props: {
        w: Math.max(Math.abs(w), 1),
        h: Math.max(Math.abs(h), 1),
      },
    }
  }

  hideRotateHandle = () => true

  onTranslateEnd = () => {
    if (this.shouldDeselectAfterTranslating) {
      this.editor.selectNone()
    }
    this.shouldDeselectAfterTranslating = false
  }
}
